import debounce from 'debounce';
import { useEffect, useCallback, useRef, useState } from 'react';

import classNames from './styles.module.css';
import { WorkPlace, StyleData } from '../../data/work';
import { config } from '../../data/config';

export interface NavigationItem {
  backgroundColor: string | null;
  name: string;
  id: string;
}

export interface NavigationBarProps {
  items: WorkPlace[];
  currentSlide: string;
  onNavigate: (id: string) => void;
}

export default function NavigationBar(props: NavigationBarProps) {
  const { currentSlide, items, onNavigate } = props;
  const navRef = useRef<HTMLElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [selectedId, setSelectedId] = useState<string>(currentSlide);
  const [selectedStyle, setSelectedStyle] = useState<StyleData | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<string>('transparent');
  const [foregroundColor, setForegroundColor] = useState<string>('transparent');
  const [selectedLeft, setSelectedLeft] = useState<number>(0);

  const changeStyle = useCallback(() => {
    if (!navRef?.current || !selectedRef?.current) {
      return;
    }

    const nav = navRef.current;
    const selected = selectedRef.current;
    const { style } = items.filter((x) => x.id === selectedId)[0] || undefined;

    setSelectedStyle(style === undefined ? null : style);

    // Update selected indicator position
    const selectedIndex = items.findIndex((item) => item.id === selectedId);
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      const itemElement = itemRefs.current[selectedIndex];
      if (itemElement) {
        const navRect = nav.getBoundingClientRect();
        const itemRect = itemElement.getBoundingClientRect();
        setSelectedLeft(itemRect.left - navRect.left + itemRect.width / 2);
      }
    }

    if (style === null) {
      nav.classList.remove(classNames.show);
      return;
    }

    if (style) {
      const bgColor =
        window.innerWidth >= config.mobileNavigatorWidth
          ? style.navigationBar.fullWidth.backgroundColor
          : style.navigationBar.mobile.backgroundColor;

      const fgColor =
        window.innerWidth >= config.mobileNavigatorWidth
          ? style.navigationBar.fullWidth.foregroundColor
          : style.navigationBar.mobile.foregroundColor;

      setBackgroundColor(bgColor);
      setForegroundColor(fgColor);

      selected.style.backgroundColor = bgColor;
      nav.style.backgroundColor = bgColor;
      nav.style.color = fgColor;
      nav.classList.add(classNames.show);
    }
  }, [items, selectedId]);

  useEffect(() => {
    changeStyle();
  }, [changeStyle]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!navRef?.current) {
        return;
      }

      const idPrefix = 'slide-';
      const slides = Array.from(document.querySelectorAll(`#slides [id^="${idPrefix}"]`));
      const nearestSlideId = slides
        .map((slide) => ({
          id: slide.getAttribute('id'),
          y: Math.abs(slide.getBoundingClientRect().top),
        }))
        .sort((a, b) => {
          if (a.y < b.y) {
            return -1;
          }
          if (a.y > b.y) {
            return 1;
          }
          return 0;
        })[0].id;

      if (nearestSlideId) {
        const id = nearestSlideId.substring(idPrefix.length);
        setSelectedId(id);
      }
    }, 50);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  useEffect(() => {
    const handleResize = () => {
      changeStyle();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [changeStyle, selectedId]);

  return (
    <nav className={classNames.outerContainer} ref={navRef} aria-label="Main navigation">
      {items.map((item, index) => (
        <span
          key={`${index}-${item.id}`}
          className={classNames.item}
          ref={(el) => (itemRefs.current[index] = el)}
          onClick={() => {
            setSelectedId(item.id);
            onNavigate(item.id);
          }}
          role="button"
          tabIndex={0}
          aria-label={`Navigate to ${item.name}`}
          aria-current={selectedId === item.id ? 'page' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedId(item.id);
              onNavigate(item.id);
            }
          }}
        >
          <span
            className={index === 0 ? classNames.homePage : classNames.maxName}
            style={{
              borderColor: foregroundColor,
            }}
          >
            {item.name}
          </span>
          {index > 0 && (
            <div
              className={selectedId === item.id ? classNames.minNameSelected : classNames.minName}
              style={{
                borderColor: foregroundColor,
                backgroundColor: selectedId === item.id ? foregroundColor : 'transparent',
              }}
            />
          )}
        </span>
      ))}
      <div
        ref={selectedRef}
        className={
          selectedStyle !== null
            ? `${classNames.selected} ${classNames.show}`
            : `${classNames.selected}`
        }
        style={{
          backgroundColor: backgroundColor,
          left: `${selectedLeft}px`,
        }}
        aria-hidden="true"
      />
    </nav>
  );
}
